import React, { memo, ReactNode } from 'react'
import SeriesTitleButton from './SeriesTitleButton'
import { Box, ButtonProps } from '@mui/material'
import { Security } from '../../../proto/generated/security'
import { StatusLineSettings } from '../chart.typings'
import TimeBuckets from '../../timeBuckets/TimeBuckets'
import ExchangeMenuButton from '../exchangeMenuButton/ExchangeMenuButton'
import OptionsChartStrikeMenu from '../../../pages/home/optionsChartHeader/optionsChartStrikeMenu/optionsChartStrikeMenu'
import OptionsChartExpiryMenu from '../../../pages/home/optionsChartHeader/optionsChartExpiryMenu/optionsChartExpiryMenu'
import FutureExpiryMenu from '../../../pages/home/futuresHeader/futureExpiryMenu'

export type SeriesMetadataPrimaryToolbarProps = {
    viewKey: string
    title?: string
    onTitleClick?: () => void
    nameContainer?: ReactNode
    security?: Security
    expressionMode: boolean
    isScalperEmbedded: boolean
    chartSettingsStatusLine?: StatusLineSettings
    showOptionExpiriesMenu: boolean
    showOptionStrikesMenu: boolean
    showFutureExpiriesMenu: boolean
    derivedDetails?: { expiryName?: string }
    chartSecurityKey?: string
}

const TIMEBUCKET_BUTTON_PROPS: ButtonProps = {
    sx: { margin: 0, padding: 0, maxHeight: '24px', fontSize: '14px' }
}

type ExchangeSlotProps = {
    security: Security
    readOnly?: boolean
}

const SeriesMetadataToolbarExchange = memo(function SeriesMetadataToolbarExchange({ security, readOnly }: ExchangeSlotProps) {
    return <ExchangeMenuButton security={security} readOnly={readOnly} />
})

type TimeBucketsSlotProps = {
    viewKey: string
}

const SeriesMetadataToolbarTimeBuckets = memo(function SeriesMetadataToolbarTimeBuckets({ viewKey }: TimeBucketsSlotProps) {
    return (
        <TimeBuckets
            registerShortcut={false}
            viewKey={viewKey}
            key={viewKey}
            showFavorites={false}
            buttonProps={TIMEBUCKET_BUTTON_PROPS}
        />
    )
})

function SeriesMetadataPrimaryToolbar({
    viewKey,
    title,
    onTitleClick,
    nameContainer,
    security,
    expressionMode,
    isScalperEmbedded,
    chartSettingsStatusLine,
    showOptionExpiriesMenu,
    showOptionStrikesMenu,
    showFutureExpiriesMenu,
    derivedDetails,
    chartSecurityKey
}: SeriesMetadataPrimaryToolbarProps) {
    const showExchange =
        !expressionMode && (chartSettingsStatusLine?.showExchange ?? true) && !!security && !isScalperEmbedded
    const exchangeReadOnly = showOptionExpiriesMenu || showFutureExpiriesMenu

    const expiryDefaultName = derivedDetails?.expiryName

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: 0.25 }}>
            {(chartSettingsStatusLine?.showSymbolName ?? true) && !isScalperEmbedded && (
                <span className="series-title">
                    {nameContainer ??
                        (title && onTitleClick ? <SeriesTitleButton title={title} onClick={onTitleClick} /> : null)}
                </span>
            )}
            {showOptionExpiriesMenu && <OptionsChartExpiryMenu defaultName={expiryDefaultName} chartSecurityKey={chartSecurityKey} />}
            {showOptionStrikesMenu && <OptionsChartStrikeMenu chartSecurityKey={chartSecurityKey} />}
            {showFutureExpiriesMenu && <FutureExpiryMenu defaultName={expiryDefaultName} chartSecurityKey={chartSecurityKey} />}
            {!isScalperEmbedded && <SeriesMetadataToolbarTimeBuckets viewKey={viewKey} />}
            {showExchange && security ? <SeriesMetadataToolbarExchange security={security} readOnly={exchangeReadOnly} /> : null}
        </Box>
    )
}

function toolbarPropsAreEqual(prev: SeriesMetadataPrimaryToolbarProps, next: SeriesMetadataPrimaryToolbarProps) {
    return (
        prev.viewKey === next.viewKey &&
        prev.title === next.title &&
        prev.onTitleClick === next.onTitleClick &&
        prev.nameContainer === next.nameContainer &&
        prev.expressionMode === next.expressionMode &&
        prev.isScalperEmbedded === next.isScalperEmbedded &&
        prev.showOptionExpiriesMenu === next.showOptionExpiriesMenu &&
        prev.showOptionStrikesMenu === next.showOptionStrikesMenu &&
        prev.showFutureExpiriesMenu === next.showFutureExpiriesMenu &&
        prev.derivedDetails?.expiryName === next.derivedDetails?.expiryName &&
        prev.chartSecurityKey === next.chartSecurityKey &&
        prev.security?.exchange === next.security?.exchange &&
        prev.security?.code === next.security?.code &&
        prev.security?.companyCode === next.security?.companyCode &&
        prev.security?.isin === next.security?.isin &&
        prev.chartSettingsStatusLine?.showSymbolName === next.chartSettingsStatusLine?.showSymbolName &&
        prev.chartSettingsStatusLine?.showExchange === next.chartSettingsStatusLine?.showExchange
    )
}

export default memo(SeriesMetadataPrimaryToolbar, toolbarPropsAreEqual)
